import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Snowflake, Mountain, Heart, Compass, Cloud, Settings2,
} from "lucide-react";

/**
 * Floating orbital menu around Creep. Every icon is a real navigation
 * entry — nothing is decorative. Uses existing app routes only.
 *
 * Props:
 *  - size: diameter (px) of the orbit container
 *  - onWeather / onSettings / onResorts: local actions
 */
export const CreepOrbit = ({
  size = 460,
  onWeather,
  onSettings,
  onResorts,
}) => {
  const navigate = useNavigate();

  const items = [
    {
      key: "snow",
      label: "Nieve",
      icon: Snowflake,
      angle: -90,
      accent: "#8A6BFF",
      testId: "orbit-snow",
      onClick: () => navigate("/map"),
    },
    {
      key: "resorts",
      label: "Estaciones",
      icon: Mountain,
      angle: -30,
      accent: "#5B2EFF",
      testId: "orbit-resorts",
      onClick: onResorts,
    },
    {
      key: "favorites",
      label: "Favoritos",
      icon: Heart,
      angle: 30,
      accent: "#D6002F",
      testId: "orbit-favorites",
      onClick: () => navigate("/favorites"),
    },
    {
      key: "map",
      label: "Mapa",
      icon: Compass,
      angle: 90,
      accent: "#8A6BFF",
      testId: "orbit-map",
      onClick: () => navigate("/map"),
    },
    {
      key: "weather",
      label: "Tiempo",
      icon: Cloud,
      angle: 150,
      accent: "#5B2EFF",
      testId: "orbit-weather",
      onClick: onWeather,
    },
    {
      key: "settings",
      label: "Ajustes",
      icon: Settings2,
      angle: 210,
      accent: "#5B2EFF",
      testId: "orbit-settings",
      onClick: onSettings,
    },
  ];

  const radius = size * 0.44;

  return (
    <div
      className="relative"
      style={{ width: size, height: size }}
      data-testid="creep-orbit"
    >
      {/* Dashed orbit ring */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none opacity-45">
        <circle
          cx="50"
          cy="50"
          r="44"
          fill="none"
          stroke="url(#orbit-grad)"
          strokeWidth="0.35"
          strokeDasharray="1 2"
        />
        <defs>
          <linearGradient id="orbit-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(138,107,255,0.9)" />
            <stop offset="100%" stopColor="rgba(91,46,255,0.2)" />
          </linearGradient>
        </defs>
      </svg>

      {/* Global slow rotation wrapper — icons counter-rotate to stay upright */}
      <div
        className="absolute inset-0"
        style={{
          animation: "creep-orbit-rot 80s linear infinite",
          transformOrigin: "50% 50%",
        }}
      >
        {items.map((it, i) => {
          const rad = (it.angle * Math.PI) / 180;
          const x = 50 + (radius / size) * 100 * Math.cos(rad);
          const y = 50 + (radius / size) * 100 * Math.sin(rad);
          return (
            <button
              key={it.key}
              data-testid={it.testId}
              aria-label={it.label}
              onClick={it.onClick}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                animation: `creep-orbit-counter 80s linear infinite, creep-orbit-bob ${5 + (i % 3)}s ease-in-out ${i * 0.3}s infinite`,
                transformOrigin: "50% 50%",
              }}
            >
              <span
                className="relative flex items-center justify-center w-14 h-14 rounded-2xl border border-white/10 transition-transform duration-200 group-hover:scale-110 group-active:scale-95"
                style={{
                  background: "rgba(11,11,18,0.72)",
                  backdropFilter: "blur(16px) saturate(140%)",
                  boxShadow: `0 0 22px ${hexToRgba(it.accent, 0.35)}, inset 0 0 0 1px rgba(255,255,255,0.04)`,
                }}
              >
                <it.icon size={22} style={{ color: it.accent, filter: `drop-shadow(0 0 6px ${hexToRgba(it.accent, 0.7)})` }} />
                <span
                  className="absolute -bottom-6 text-[10px] uppercase tracking-widest text-white/70 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  {it.label}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

export default CreepOrbit;
